import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Timer, Zap, ShieldCheck, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";
import { formatINR } from "@/lib/api";

const ISHUSTAR_PRICE = 25524000;
const ISHUSTAR_TOKEN = 500000;

const SHOTS = [
  { src: "/images/ishustar-hero.jpg", label: "Liquid Silver · Front" },
  { src: "/images/ishustar-side.jpg", label: "Liquid Silver · Profile" },
  { src: "/images/ishustar-rear.jpg", label: "Liquid Silver · Rear" },
];

const SPECS = [
  { icon: Zap, value: "100%", label: "Electric Hypercar" },
  { icon: Timer, value: "STD", label: "Single Variant · Limited" },
  { icon: ShieldCheck, value: formatINR(ISHUSTAR_TOKEN), label: "Refundable Booking" },
  { icon: Users, value: "2 Seater", label: "Driver-First Cockpit" },
];

function Carousel() {
  const [idx, setIdx] = useState(0);
  const go = (d) => setIdx((i) => (i + d + SHOTS.length) % SHOTS.length);
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10" data-testid="ishustar-carousel">
      <AnimatePresence mode="wait">
        <motion.img
          key={SHOTS[idx].src}
          src={SHOTS[idx].src}
          alt={`IshuCar ISHUSTAR — ${SHOTS[idx].label}`}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="h-64 w-full object-cover sm:h-96"
          data-testid="ishustar-carousel-image"
        />
      </AnimatePresence>
      <button
        onClick={() => go(-1)}
        data-testid="ishustar-carousel-prev"
        aria-label="Previous image"
        className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-sm transition-colors hover:border-[#e6b800] hover:text-[#e6b800]"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={() => go(1)}
        data-testid="ishustar-carousel-next"
        aria-label="Next image"
        className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-sm transition-colors hover:border-[#e6b800] hover:text-[#e6b800]"
      >
        <ChevronRight size={18} />
      </button>
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-[#070709]/90 to-transparent px-5 py-4">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-300">{SHOTS[idx].label}</p>
        <div className="flex gap-1.5">
          {SHOTS.map((s, i) => (
            <button
              key={s.src}
              onClick={() => setIdx(i)}
              aria-label={`Go to image ${i + 1}`}
              data-testid={`ishustar-carousel-dot-${i}`}
              className={`h-2 w-2 rounded-full transition-all duration-300 ${i === idx ? "w-5 bg-[#e6b800]" : "bg-white/30 hover:bg-white/60"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-36 pb-16" data-testid="ishustar-hero">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse, rgba(230,184,0,0.14), transparent 62%)" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="eyebrow">IshuCar ISHUSTAR · 100% Electric Hypercar</p>
          <h1 className="mt-4 font-display text-[clamp(1.6rem,6vw,3rem)] font-extrabold uppercase leading-[1.08] tracking-tight text-white">
            Speed meets <span className="text-gold-gradient">soul</span>
          </h1>
          <p className="mt-5 max-w-lg text-base text-neutral-400 sm:text-lg">
            सपनों का हाइपरकार अब हक़ीक़त में — sharp aero design, driver-first cockpit और पूरी तरह बिजली पर।
            Limited units — जो पहले, उनकी पहले।
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/booking/ishustar"
              data-testid="ishustar-reserve-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-8 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
            >
              Reserve for {formatINR(ISHUSTAR_TOKEN)}
              <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/gallery"
              data-testid="ishustar-gallery-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 font-semibold text-white transition-all duration-200 hover:border-[#e6b800]/60 hover:text-[#e6b800]"
            >
              Gallery देखें
            </Link>
          </div>
          <p className="mt-5 font-mono2 text-[12px] uppercase tracking-[0.2em] text-neutral-500" data-testid="ishustar-price-line">
            Ex-showroom {formatINR(ISHUSTAR_PRICE)}* · Booking refundable
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <Carousel />
        </Reveal>
      </div>
    </section>
  );
}

function BookingBreakdown() {
  return (
    <section className="border-y border-white/10 bg-[#0a0a0e] py-24 lg:py-28" data-testid="ishustar-breakdown-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="How booking works"
          title={`आज ${formatINR(ISHUSTAR_TOKEN)} — बाकी बाद में`}
          sub="Tesla-style reservation: आज refundable booking amount भरें, बाकी balance 10 दिन के अंदर।"
        />
        <div className="mx-auto mt-14 grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-2">
          <Reveal>
            <div className="card-glow h-full rounded-2xl p-8 text-center" data-testid="ishustar-due-today-card">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">Due today · Fully refundable</p>
              <p className="text-gold-gradient mt-4 font-display text-3xl font-extrabold sm:text-5xl">{formatINR(ISHUSTAR_TOKEN)}</p>
              <p className="mt-3 text-sm text-neutral-400">Booking token — Razorpay से secure payment। Cancel करें तो पैसा वापस।</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#111116] p-8 text-center" data-testid="ishustar-due-later-card">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">Due within 10 days</p>
              <p className="mt-4 font-display text-3xl font-extrabold text-white sm:text-5xl">
                {formatINR(ISHUSTAR_PRICE - ISHUSTAR_TOKEN)}
              </p>
              <p className="mt-3 text-sm text-neutral-400">
                बाकी balance — payment न होने पर reservation cancel हो सकता है।
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal className="mx-auto mt-8 max-w-4xl">
          <p className="text-[11px] leading-relaxed text-neutral-600" data-testid="ishustar-disclaimer">
            Due to limited availability, ISHUSTAR is restricted to one order per customer. We reserve the right to
            cancel duplicate orders, multiple purchases using the same account or phone number, and orders sharing
            identical payment or address details. Reservations are not final until payment is received. Availability
            is subject to each region&apos;s legal requirements and applicable laws.
          </p>
        </Reveal>
        <Reveal className="mt-10 text-center">
          <Link
            to="/booking/ishustar"
            data-testid="ishustar-breakdown-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-9 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
          >
            Reserve your ISHUSTAR
            <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function SpecsSection() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="ishustar-specs">
      <SectionHeading
        eyebrow="Specs · खूबियाँ"
        title="Hypercar, हर हिसाब से"
        sub="Full technical specifications जल्द ही — pre-book करने वालों को सबसे पहले बताएंगे।"
      />
      <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {SPECS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.06}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#111116] p-5 text-center transition-colors duration-300 hover:border-[#e6b800]/50" data-testid={`ishustar-spec-${i}`}>
              <s.icon size={20} className="mx-auto text-[#e6b800]" />
              <p className="mt-3 font-display text-base font-extrabold text-white sm:text-lg">{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-neutral-500">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-center text-[11px] text-neutral-600">
        *Ex-showroom price, indicative — insurance, RTO and on-road charges extra.
      </p>
    </section>
  );
}

function VideoSection() {
  return (
    <section className="relative overflow-hidden" data-testid="ishustar-video-section">
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="/images/ishustar-hero.jpg"
        data-testid="ishustar-video"
        className="h-[52vh] w-full object-cover opacity-60 sm:h-[70vh]"
        aria-hidden="true"
      >
        <source src="/videos/ishustar-hero.mp4" type="video/mp4" />
        <source src="/videos/ishustar-hero.webm" type="video/webm" />
      </video>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#070709]/70 via-transparent to-[#070709]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-end pb-12 text-center sm:pb-16">
        <p className="eyebrow">The machine in motion</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
          हवा से <span className="text-gold-gradient">तेज़, बिजली पर</span>
        </h2>
      </div>
    </section>
  );
}

export default function Ishustar() {
  return (
    <div data-testid="ishustar-page">
      <Hero />
      <VideoSection />
      <BookingBreakdown />
      <SpecsSection />
    </div>
  );
}
